import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Upload, Row, Col } from 'antd';
import { UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { AddOrgRequest } from '../../interfaces/orgManagement';
import orgManagementService from '../../apiServices/orgManagementService';
import coreAPIService from '../../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../../utils';
import { uploadMedia } from '../../utils/campaignUtils';

interface AddOrgModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}
interface CreateBrandResponse {
    meta: {
        status: boolean;
        message: string;
        code: number;
        newToken: string | null;
    };
    data?: {
        brand_id: number;
    };
}

const AddOrgModal: React.FC<AddOrgModalProps> = ({
    visible,
    onCancel,
    onSuccess,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (file: File) => {
        const orgId = form.getFieldValue('orgId');
        if (!orgId) {
            message.error('Please enter Account ID first');
            return Upload.LIST_IGNORE;
        }

        try {
            setUploading(true);
            const url = await uploadMedia(file, orgId, '');
            if (url) {
                form.setFieldsValue({ orgImg: url });
                message.success('Image uploaded successfully');
            } else {
                message.error('Failed to upload image');
            }
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Failed to upload image');
        } finally {
            setUploading(false);
        }
        return false; // Prevent default upload behavior
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 1. Check if org exists
            // const exists = await orgManagementService.checkOrgExists(values.orgId);
            // if (exists) {
            //     message.error('Account with this ID already exists!');
            //     setLoading(false);
            //     return;
            // }

            // 2. Add organization
            await orgManagementService.addOrg(values.orgId);
            message.success('Account added successfully');
            const brandPayload = {
                orgId: values.orgId,
                business_entity_id: -1,
                brand_name: values.orgName,
                website: '',
                audience_location: ['IN'],
                brandLogo: values.orgImg,
                languages: [],
                social_media_handles: {
                    facebook: '',
                    instagram: '',
                    twitter: '',
                    whatsapp: ''
                },
                about: values.orgAbout,
                online_store: '',
                mau: 0,
                site_category: [],
                other_category: ''
            };
            const response = await coreAPIService.post<CreateBrandResponse>(
                CTW_API_ENDPOINT,
                'v2/verismart/create-brand',
                brandPayload
            );
            form.resetFields();
            onSuccess();
        } catch (error) {
            console.error('Error adding org:', error);
            message.error('Failed to add account');
        } finally {
            setLoading(false);
        }
    };

    const orgImg = Form.useWatch('orgImg', form);

    return (
        <Modal
            title="Add New Account"
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={loading}
            okText="Verify & Add"
            okButtonProps={{ className: 'bg-[#1F344C]' }}
        >
            <Form
                form={form}
                layout="vertical"
            >
                <Form.Item
                    name="orgName"
                    label="Account Name"
                    rules={[{ required: true, message: 'Please enter account name' }]}
                >
                    <Input placeholder="e.g. Acme Corp" />
                </Form.Item>
                <Form.Item
                    name="orgId"
                    label="Account ID"
                    rules={[
                        { required: true, message: 'Please enter account ID' },
                        { pattern: /^[a-zA-Z0-9-_]+$/, message: 'Only alphanumeric characters, hyphens and underscores allowed' }
                    ]}
                    help="Unique identifier for the account"
                >
                    <Input placeholder="e.g. acme-corp-001" />
                </Form.Item>
                <Form.Item
                    name="orgImg"
                    label="Account Image"
                    rules={[{ required: true, message: 'Please enter account image' }]}
                >
                    <div className="flex flex-col gap-2">
                        <Row gutter={8}>
                            <Col flex="auto">
                                <Input placeholder="e.g. https://example.com/acme-corp.png" value={orgImg} onChange={(e) => form.setFieldsValue({ orgImg: e.target.value })} />
                            </Col>
                            <Col>
                                <Upload
                                    beforeUpload={handleUpload}
                                    showUploadList={false}
                                    maxCount={1}
                                >
                                    <Button icon={uploading ? <LoadingOutlined /> : <UploadOutlined />}>
                                        {uploading ? 'Uploading...' : 'Upload'}
                                    </Button>
                                </Upload>
                            </Col>
                        </Row>
                        {orgImg && (
                            <div className="mt-2">
                                <img
                                    src={orgImg}
                                    alt="Preview"
                                    style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '4px' }}
                                />
                            </div>
                        )}
                    </div>
                </Form.Item>

                <Form.Item
                    name="orgAbout"
                    label="Account Description"
                    rules={[{ required: true, message: 'Please enter account description' }]}
                >
                    <Input placeholder="About Account" onChange={(e) => form.setFieldsValue({ orgAbout: e.target.value })} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddOrgModal;
